/**
 * Line-open chime (2026-10-07). When a prospect is passed to the caller, the
 * prospect has usually said "allô" already, while the line was held for
 * screening, and waits for an answer: the caller heard nothing and waited
 * too (1.6 to 2.9 s of silence on the calls of that day). A short, soft
 * chime tells the caller the line is open: speak now.
 *
 * Soft on purpose (Kevin: "un bip agréable, pas un truc qui détruit les
 * oreilles"): two sine notes a fourth apart (E5 then A5), 15 ms fade-in,
 * 300 ms fade-out, peak gain 0.05, about 0.4 s in all. Respects the
 * application's sound setting, like every other sound (#23).
 */
import { sonEstActif } from '@/state/soundPrefs';

let context: AudioContext | null = null;

function audioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (context && context.state !== 'closed') return context;
  const Ctor = window.AudioContext
    ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  try { context = new Ctor(); } catch { context = null; }
  return context;
}

function note(ctx: AudioContext, frequency: number, start: number): void {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.value = frequency;
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(0.05, start + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.3);
  osc.connect(gain).connect(ctx.destination);
  osc.start(start);
  osc.stop(start + 0.32);
}

export function playLineOpenChime(): void {
  if (!sonEstActif()) return;
  const ctx = audioContext();
  if (!ctx) return;
  try {
    if (ctx.state === 'suspended') void ctx.resume();
    const t0 = ctx.currentTime + 0.01;
    note(ctx, 659.25, t0);
    note(ctx, 880, t0 + 0.09);
  } catch { /* a chime must never break a call */ }
}
